import { FormikProps } from 'formik';
import { Col, Flex, Form, Row } from 'antd';
import { memo } from 'react';

import dayjs from 'dayjs';
import { MDatePicker, MRadio, MSelect, MTextArea } from '@Components/Base';
import { Option } from '@Components/types/options';
import { prepareDatePickerValue } from '@Utils/transformDateTime';
import { LanguageCodes } from '@Features/EmployeeSelfService/requests/SalaryCertificate/languages';

import { SalaryCertificateFormValues } from '../types';

interface RequestDetailsSectionProps {
  formik: FormikProps<SalaryCertificateFormValues>;
  label: string;
  formApproval: boolean;
  salaryTypeOptions: Option[];
}

const RequestDetailsSection: React.FC<RequestDetailsSectionProps> = ({
  formik,
  formApproval,
  salaryTypeOptions,
}) => {
  const {
    purpose: purposeTouched,
    remarks: remarksTouched,
    language: languageTouched,
    requiredByDate: requiredByDateTouched,
  } = formik.touched;
  const {
    purpose: purposeError,
    remarks: remarksError,
    language: languageError,
    requiredByDate: requiredByDateError,
  } = formik.errors;

  return (
    <Row>
      <Col span={24}>
        <Form.Item
          label="Purpose (Reason)"
          validateStatus={purposeTouched && purposeError ? 'error' : ''}
          help={purposeTouched && purposeError ? purposeError : ''}
        >
          <MSelect
            labeloption="Purpose"
            options={salaryTypeOptions}
            onChange={(selectValue) =>
              formik.setFieldValue('purpose', selectValue)
            }
            onBlur={() => {
              formik.setFieldTouched('purpose', true);
            }}
            value={formik.values.purpose}
            disabled={formApproval}
            placeholder="Select Purpose"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Remarks"
          validateStatus={remarksTouched && remarksError ? 'error' : ''}
          help={remarksTouched && remarksError ? remarksError : ''}
        >
          <MTextArea
            name="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            onBlur={() => {
              formik.setFieldTouched('remarks', true);
            }}
            rows={2}
            showCount
            maxLength={100}
            disabled={formApproval}
            placeholder="Leave a comment"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Required Language"
          validateStatus={languageTouched && languageError ? 'error' : ''}
          help={languageTouched && languageError ? languageError : ''}
        >
          <Flex>
            <MRadio
              id="Arabic"
              value={LanguageCodes.Arabic}
              name="language"
              labelText="Arabic"
              onChange={(event) =>
                formik.setFieldValue('language', event.target.value)
              }
              onBlur={() => {
                formik.setFieldTouched('language', true);
              }}
              checked={formik.values.language === LanguageCodes.Arabic}
            />
            <MRadio
              id="English"
              value={LanguageCodes.English}
              name="language"
              labelText="English"
              onChange={(event) =>
                formik.setFieldValue('language', event.target.value)
              }
              onBlur={() => {
                formik.setFieldTouched('language', true);
              }}
              checked={formik.values.language === LanguageCodes.English}
            />
          </Flex>
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label="Required By"
          validateStatus={
            requiredByDateTouched && requiredByDateError ? 'error' : ''
          }
          help={
            requiredByDateTouched && requiredByDateError
              ? requiredByDateError
              : ''
          }
        >
          <MDatePicker
            className="leave-deatails-datepicker"
            name="requiredByDate"
            value={
              formik.values.requiredByDate &&
              dayjs(formik.values.requiredByDate).tz('UTC')
            }
            format="DD/MM/YYYY"
            onChange={(value) => {
              formik.setFieldValue(
                'requiredByDate',
                prepareDatePickerValue(value, true)
              );
            }}
            disabledDate={(current) => {
              // Disable past days
              return current && current < dayjs().startOf('day');
            }}
            onBlur={() => {
              formik.setFieldTouched('requiredByDate', true);
            }}
            disabled={formApproval}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default memo(RequestDetailsSection);
